/**
 * Leaderboard Service
 * 
 * Manages leaderboard data derived from public profiles.
 * This is a read-only collection for clients (updated via sync functions).
 * 
 * Collection: /leaderboards/global/{userId}
 */

import { doc, setDoc, collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { PublicProfile } from './publicProfileService';

export interface LeaderboardEntry {
  id: string;
  userId: string;
  name: string;
  avatar: string;
  level: number;
  xp: number;
  title?: string; // Optional title from public profile
  rank?: number;
  updatedAt: Date;
}

/**
 * Sync public profile to leaderboard
 * Call this whenever user XP or level changes
 */
export const syncToLeaderboard = async (userId: string, profileData: PublicProfile): Promise<void> => {
  try {
    // Only sync if profile is public
    if (profileData.isPublic !== true) {
      return;
    }
    
    // Path: /leaderboards/global/{userId}
    const leaderboardRef = doc(db, 'leaderboards', 'global', userId);
    
    const leaderboardEntry: Omit<LeaderboardEntry, 'id'> = {
      userId,
      name: profileData.name,
      avatar: profileData.avatar,
      level: profileData.level,
      xp: profileData.xp,
      title: profileData.title, // Include title if available
      updatedAt: new Date()
    };
    
    await setDoc(leaderboardRef, leaderboardEntry, { merge: true });
    
    if (import.meta.env.DEV) {
      console.log('[LEADERBOARD] ✅ Synced to leaderboard:', userId);
    }
  } catch (error: any) {
    // Log but don't throw - this is a non-critical sync operation
    if (import.meta.env.DEV) {
      console.error('[LEADERBOARD] ❌ Error syncing to leaderboard:', {
        userId,
        code: error?.code,
        message: error?.message
      });
    }
  }
};

/**
 * Get leaderboard entries (sorted by XP descending)
 * Returns top N entries
 */
export const getLeaderboard = async (limitCount: number = 20): Promise<LeaderboardEntry[]> => {
  try {
    const leaderboardRef = collection(db, 'leaderboards', 'global');
    const q = query(
      leaderboardRef,
      orderBy('xp', 'desc'),
      orderBy('level', 'desc'),
      limit(limitCount)
    );
    
    const snapshot = await getDocs(q);
    
    const entries = snapshot.docs
      .map((doc, index) => ({
        id: doc.id,
        ...doc.data(),
        rank: index + 1
      } as LeaderboardEntry));
    
    return entries;
  } catch (error: any) {
    if (import.meta.env.DEV) {
      console.error('[LEADERBOARD] Error getting leaderboard:', {
        code: error?.code,
        message: error?.message
      });
    }
    return [];
  }
};

/**
 * Get leaderboard entries (one-shot fetch)
 * 🔒 CRITICAL: Uses one-shot getDocs() instead of onSnapshot to avoid permission-denied errors
 * Leaderboard is public data - no realtime needed
 */
export const getLeaderboardEntries = async (limitCount: number = 20): Promise<LeaderboardEntry[]> => {
  try {
    const leaderboardRef = collection(db, 'leaderboards', 'global');
    const q = query(
      leaderboardRef,
      orderBy('xp', 'desc'),
      orderBy('level', 'desc'),
      limit(limitCount)
    );
    
    const snapshot = await getDocs(q);
    
    const entries = snapshot.docs
      .map((doc, index) => ({
        id: doc.id,
        ...doc.data(),
        rank: index + 1
      } as LeaderboardEntry));
    
    return entries;
  } catch (error: any) {
    // CRITICAL: Handle errors gracefully - don't crash the app
    if (import.meta.env.DEV) {
      console.error('[LEADERBOARD] Error fetching leaderboard:', {
        code: error?.code,
        message: error?.message
      });
    }
    return [];
  }
};

